import { useState } from "react";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";

import { NAV_LINKS } from "MVVM/Components/Sidebar/utils";
import { TrophyBadge } from "MVVM/Components/TrophyBadge";
import {
  stepsToNextTier,
  TLimitTrophy,
  TROPHY_GAIN_CAP,
} from "MVVM/Hooks/useLimitTrophies";
import { getTrophyTierColor, TROPHY_TIER_LABEL } from "MVVM/Utilities/Trophy";

interface ITrophyShelfProps {
  trophies: TLimitTrophy[];
  closest: TLimitTrophy | null;
  gain: number;
  maxed: boolean;
  onOpen: () => void;
}

const describe = (trophy: TLimitTrophy, maxed: boolean) => {
  const next = trophy.progress?.nextTier;
  if (!next) return `${trophy.name}: every tier earned.`;
  const reward = maxed ? "" : " Earns +1 clip a month, for good.";
  return `${trophy.name} ${TROPHY_TIER_LABEL[next]}: ${stepsToNextTier(
    trophy,
  )} to go. ${trophy.goal}.${reward}`;
};

// One strip under the shelf explains whichever badge is pointed at, and the
// closest unlock otherwise, so nothing floats outside the narrow rail.
export const TrophyShelf = ({
  trophies,
  closest,
  gain,
  maxed,
  onOpen,
}: ITrophyShelfProps) => {
  const [pointed, setPointed] = useState<string | null>(null);
  const focus =
    trophies.find((trophy) => trophy.key === pointed) ?? closest ?? trophies[0];
  if (!focus) return null;

  const next = focus.progress?.nextTier;
  const accent = getTrophyTierColor(next ?? focus.tier ?? undefined);

  return (
    <Shelf aria-labelledby="clip-earn-trophies">
      <div>
        <Head>
          <h4 id="clip-earn-trophies">Trophies</h4>
          <span>
            <strong>+{gain}</strong> of {TROPHY_GAIN_CAP} bonus clips
          </span>
        </Head>
        <p>
          {maxed
            ? "You've maxed your trophy bonus. Trophies still track your progress."
            : "Complete trophies to increase your clip limit."}
        </p>
      </div>

      <Badges onMouseLeave={() => setPointed(null)}>
        {trophies.map((trophy) => {
          const { progress } = trophy;
          return (
            <li key={trophy.key}>
              <Tile
                to={NAV_LINKS.rewards}
                aria-label={describe(trophy, maxed)}
                $active={trophy.key === focus.key}
                $dimmed={!!pointed && trophy.key !== pointed}
                onMouseEnter={() => setPointed(trophy.key)}
                onFocus={() => setPointed(trophy.key)}
                onBlur={() => setPointed(null)}
                onClick={onOpen}
              >
                <TrophyBadge
                  trophyKey={trophy.key}
                  tier={trophy.tier ?? undefined}
                  progress={progress}
                  size="md"
                />
                <Name>{trophy.name}</Name>
                <Count aria-hidden="true">
                  {progress ? (
                    <>
                      <b>{progress.current}</b>/{progress.target}
                    </>
                  ) : (
                    "Maxed"
                  )}
                </Count>
              </Tile>
            </li>
          );
        })}
      </Badges>

      <Detail $accent={accent} aria-hidden="true">
        <div>
          <p>
            {next ? "Next: " : ""}
            <strong>{focus.name}</strong>{" "}
            {next ? TROPHY_TIER_LABEL[next] : "complete"}
          </p>
          <p>
            {next
              ? `${stepsToNextTier(focus)} to go. ${focus.goal}`
              : "Every tier earned."}
          </p>
        </div>
        {next && !maxed && (
          <Reward>
            +1 <small>clip a month</small>
          </Reward>
        )}
        {next && maxed && <Capped>Bonus clips maxed</Capped>}
      </Detail>
    </Shelf>
  );
};

const Shelf = styled.section`
  display: grid;
  gap: 12px;
  padding: 14px 12px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  background: radial-gradient(
      120% 80% at 50% 0%,
      ${({ theme }) => theme.colors.chalkAlpha10} 0%,
      transparent 70%
    ),
    ${({ theme }) => theme.colors.navy300};
`;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding: 0 2px;

  span {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 12px;
    font-variant-numeric: tabular-nums;
  }

  strong {
    color: ${({ theme }) => theme.colors.envy};
    font-weight: 700;
  }
`;

const Badges = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 4px;
`;

const ease = "cubic-bezier(0.16, 0.84, 0.34, 1)";

const Tile = styled(Link)<{ $active: boolean; $dimmed: boolean }>`
  display: grid;
  justify-items: center;
  align-content: start;
  gap: 6px;
  height: 100%;
  padding: 8px 2px 6px;
  border-radius: 8px;
  color: inherit;
  text-decoration: none;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.55 : 1)};
  transition:
    background-color 0.2s ${ease},
    opacity 0.2s ${ease};

  > :first-child {
    transition: transform 0.25s ${ease};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.chalkAlpha10};

      > :first-child {
        transform: translateY(-2px);
      }
    `}

  &:hover {
    color: inherit;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 1px;
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    > :first-child {
      transition: none;
    }
  }
`;

const Name = styled.span`
  max-width: 100%;
  color: ${({ theme }) => theme.colors.ice};
  font-size: 12px;
  font-weight: 700;
  line-height: 14px;
  text-align: center;
  text-wrap: balance;
`;

const Count = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  line-height: 14px;
  font-variant-numeric: tabular-nums;

  b {
    color: ${({ theme }) => theme.colors.ice};
    font-weight: 700;
  }
`;

const Detail = styled.div<{ $accent: string }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-height: 54px;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.navy600};
  box-shadow: inset 0 1px 0 ${({ $accent }) => $accent}33;

  p:first-child {
    margin: 0;
    color: ${({ $accent }) => $accent};
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  strong {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const Reward = styled.span`
  font-family: Barlow, sans-serif;
  font-style: italic;
  font-weight: 900;
  font-size: 18px;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.envy};
  white-space: nowrap;
  text-align: right;

  small {
    display: block;
    font-style: normal;
    font-weight: 600;
    font-size: 10px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.chalk};
  }
`;

const Capped = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  white-space: nowrap;
`;
